import { useState } from "react";
import { MapPin, Sparkles, Star, X, Utensils } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Renders a list of restaurant recommendations inside the YumoPot drawer.
 * Supports:
 *  - Multi-select (up to 3) for Comparison Mode
 *  - Per-card "Save" (sends feedback signal into Taste DNA loop)
 *  - Inline comparison table that renders under the cards when >=2 selected
 */
function ScoreBar({ label, value }) {
  return (
    <div className="flex items-center gap-2 text-[11px]" data-testid={`compare-axis-${label}`}>
      <div className="w-16 text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${value}%` }} />
      </div>
      <div className="w-8 text-right font-medium">{value}</div>
    </div>
  );
}

export default function RestaurantResults({ payload, intent, onFeedback }) {
  const [selected, setSelected] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [saved, setSaved] = useState({});

  const toggleSelect = (osmId) => {
    setSelected((prev) => {
      if (prev.includes(osmId)) return prev.filter((x) => x !== osmId);
      if (prev.length >= 3) return prev;
      return [...prev, osmId];
    });
    setComparison(null);
  };

  const runCompare = async () => {
    if (selected.length < 2) return;
    setComparing(true);
    try {
      const { data } = await api.post("/restaurants/compare", { intent, osm_ids: selected });
      setComparison(data);
    } catch (e) {
      setComparison({ error: e.response?.data?.detail || "Comparison failed. Try again." });
    } finally {
      setComparing(false);
    }
  };

  const saveRestaurant = async (r) => {
    setSaved((s) => ({ ...s, [r.osm_id]: true }));
    try {
      await api.post("/restaurants/feedback", { osm_id: r.osm_id, action: "saved", intent });
      onFeedback?.("saved", r);
    } catch { /* no-op */ }
  };

  const restaurants = payload.results || [];

  return (
    <div className="w-full space-y-3" data-testid="restaurant-results">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Sparkles className="w-3.5 h-3.5 text-primary" />
        <span>Found {restaurants.length} match{restaurants.length === 1 ? "" : "es"}. Tap up to 3 to compare.</span>
      </div>

      {restaurants.map((r) => {
        const isSel = selected.includes(r.osm_id);
        return (
          <div
            key={r.osm_id}
            className={`rounded-2xl border p-4 transition ${
              isSel ? "border-primary bg-primary/5" : "border-border bg-card"
            }`}
            data-testid={`restaurant-card-${r.osm_id.replace('/','-')}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-serif text-lg font-semibold tracking-tight leading-tight" data-testid="restaurant-name">
                  {r.name}
                </div>
                <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                  {r.distance_km != null && (
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{r.distance_km} km</span>
                  )}
                  {r.cuisine?.length > 0 && (
                    <span className="flex items-center gap-1"><Utensils className="w-3 h-3" />{r.cuisine.slice(0, 2).join(", ")}</span>
                  )}
                </div>
              </div>
              <div
                className="shrink-0 rounded-full bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 flex items-center gap-1"
                data-testid="restaurant-match-score"
              >
                <Star className="w-3 h-3 fill-primary" />
                {r.match_score}%
              </div>
            </div>

            <p className="mt-2 text-[13px] text-foreground/85 leading-relaxed">{r.why_recommended}</p>

            {r.reasons?.length > 0 && (
              <ul className="mt-2 space-y-1 text-[12px] text-foreground/70">
                {r.reasons.slice(0, 3).map((x, i) => (
                  <li key={i} className="flex gap-1.5"><span className="text-primary">•</span>{x}</li>
                ))}
              </ul>
            )}

            {(r.address || r.opening_hours) && (
              <div className="mt-2 text-[11px] text-muted-foreground">
                {r.address && <div>{r.address}</div>}
                {r.opening_hours && <div>Hours: {r.opening_hours}</div>}
              </div>
            )}

            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => toggleSelect(r.osm_id)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  isSel ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
                }`}
                data-testid={`compare-toggle-${r.osm_id.replace('/','-')}`}
              >
                {isSel ? "Selected for compare" : "Compare"}
              </button>
              <button
                onClick={() => saveRestaurant(r)}
                disabled={saved[r.osm_id]}
                className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-secondary transition disabled:opacity-60"
                data-testid={`save-restaurant-${r.osm_id.replace('/','-')}`}
              >
                {saved[r.osm_id] ? "Saved" : "Save"}
              </button>
              {r.website && (
                <a href={r.website} target="_blank" rel="noopener noreferrer"
                   className="text-xs text-primary hover:underline ml-auto" data-testid="restaurant-website">
                  Website ↗
                </a>
              )}
            </div>
          </div>
        );
      })}

      {selected.length >= 2 && !comparison && (
        <button
          onClick={runCompare}
          disabled={comparing}
          className="w-full py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium disabled:opacity-70"
          data-testid="run-comparison-btn"
        >
          {comparing ? "Comparing…" : `Compare ${selected.length} restaurants`}
        </button>
      )}

      {comparison && (
        <div className="rounded-2xl border border-border p-4 bg-secondary/30" data-testid="restaurant-comparison">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">YUMO Comparison</div>
            <button onClick={() => setComparison(null)} className="text-muted-foreground" aria-label="Close" data-testid="close-comparison">
              <X className="w-4 h-4" />
            </button>
          </div>
          {comparison.error ? (
            <div className="text-sm text-destructive">{comparison.error}</div>
          ) : (
            <>
              <div className="space-y-3">
                {comparison.table.map((row) => (
                  <div key={row.osm_id} className="border border-border rounded-xl p-3 bg-background">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-sm">{row.name}</div>
                      <div className="text-xs font-medium text-primary">{row.match_score}%</div>
                    </div>
                    <div className="space-y-1">
                      {Object.entries(row.axes).map(([k, v]) => (
                        <ScoreBar key={k} label={k.replace("_", " ")} value={v} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-xl bg-primary/10 border border-primary/20" data-testid="yumo-pick">
                <div className="overline text-primary mb-1">YUMO Pick</div>
                <div className="font-semibold text-sm">{comparison.yumo_pick.name}</div>
                <div className="text-xs text-foreground/80 mt-1">{comparison.reason}</div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
